if (!customElements.get('sticky-offer-bar')) {
  class StickyOfferBar extends HTMLElement {
    isPastAnchor = false;
    visibleSuppressors = new Set();

    connectedCallback() {
      // The price follows the page's purchase options (Subscribe / One-time) when present.
      document.addEventListener('bundle-purchase-options:price-change', this.onPriceChange);
      this.syncInitialPrice();

      // In the theme editor the bar stays visible so merchants can edit it.
      if (window.Shopify?.designMode) return;

      this.panel = this.querySelector('.sticky-offer-bar__panel');
      this.anchor = [...document.querySelectorAll('main .shopify-section')].find((section) => !section.contains(this));
      if (!this.panel || !this.anchor) return;

      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.isPastAnchor = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.update();
      });
      this.intersectionObserver.observe(this.anchor);

      // Sections that carry their own buy UI opt out of the bar while they are on screen.
      this.suppressorObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.visibleSuppressors.add(entry.target);
          } else {
            this.visibleSuppressors.delete(entry.target);
          }
        }
        this.update();
      });
      for (const suppressor of document.querySelectorAll('[data-sticky-offer-bar-hide]')) {
        this.suppressorObserver.observe(suppressor);
      }

      this.resizeObserver = new ResizeObserver(() => {
        document.body.style.setProperty('--sticky-offer-bar-height', `${this.panel.offsetHeight}px`);
      });
      this.resizeObserver.observe(this.panel);
    }

    disconnectedCallback() {
      document.removeEventListener('bundle-purchase-options:price-change', this.onPriceChange);
      this.intersectionObserver?.disconnect();
      this.suppressorObserver?.disconnect();
      this.visibleSuppressors.clear();
      this.resizeObserver?.disconnect();
      document.body.classList.remove('sticky-offer-bar-open');
      document.body.style.removeProperty('--sticky-offer-bar-height');
    }

    /**
     * @param {CustomEvent<{price: string, compareAtPrice: string}>} event
     * @returns {void}
     */
    onPriceChange = (event) => {
      this.setPrice(event.detail?.price, event.detail?.compareAtPrice);
    };

    /**
     * <bundle-purchase-options> may announce its price before this bar connects,
     * so start from the option that is already selected.
     * @returns {void}
     */
    syncInitialPrice() {
      const selected = document.querySelector('bundle-purchase-options input[name="bundle_purchase_option"]:checked');
      if (!selected) return;

      this.setPrice(selected.dataset.variantPrice, selected.dataset.variantCompareAtPrice);
    }

    /**
     * Shows the active option's price; hides the compare price when it matches (One-time).
     * @param {string} [price]
     * @param {string} [compareAtPrice]
     * @returns {void}
     */
    setPrice(price, compareAtPrice) {
      const priceElement = this.querySelector('[data-sticky-offer-price]');
      if (!priceElement || !price) return;

      priceElement.textContent = price;

      const compareElement = this.querySelector('[data-sticky-offer-compare]');
      const compareValue = compareElement?.querySelector('[data-sticky-offer-compare-value]');
      if (!compareValue) return;

      const hasCompare = Boolean(compareAtPrice) && compareAtPrice !== price;
      compareElement.hidden = !hasCompare;
      if (hasCompare) compareValue.textContent = compareAtPrice;
    }

    /**
     * Shows the bar once the first section has scrolled above the viewport,
     * unless a `[data-sticky-offer-bar-hide]` element is on screen.
     * @returns {void}
     */
    update() {
      this.toggle(this.isPastAnchor && this.visibleSuppressors.size === 0);
    }

    /**
     * @param {boolean} isVisible
     * @returns {void}
     */
    toggle(isVisible) {
      this.toggleAttribute('data-visible', isVisible);
      this.inert = !isVisible;
      document.body.classList.toggle('sticky-offer-bar-open', isVisible);
    }
  }

  customElements.define('sticky-offer-bar', StickyOfferBar);
}
